/**
 * FAQ content, shared between the marketing landing FAQ section
 * (routes/Marketing.tsx) and the standalone /faq page (routes/Faq.tsx) so the
 * two never drift.
 *
 * Plain English only — this is public marketing copy, so it follows the IP
 * boundary in docs/COPY-GUIDELINES.md: no internal vocabulary (no "trapline",
 * "signatures", "calibrate", storage primitives) and no em-dashes.
 *
 * The first item is the one students most often get wrong about the product:
 * they assume the drills are a bank of real exam questions. They are not, by
 * design. Keep it first.
 */
import { CORPUS_TOTAL_LABEL } from "./corpus-label.js";

export type FaqItem = {
  q: string;
  a: string;
  /**
   * Show on the marketing landing page's short FAQ. The landing page carries a
   * handful of the questions a first-time visitor actually asks before they
   * commit; the full list lives on /faq so the landing page does not end in a
   * wall of text. Keep this to about six items.
   */
  landing?: boolean;
};

export const FAQ_ITEMS: FaqItem[] = [
  {
    landing: true,
    q: "Are these the real questions from the SAT?",
    a: "No, and that is the whole point. The drills are written to look and feel like the real test, but they are not a bank of leaked questions to memorize. Each one is built around a specific way the real exam tries to trip you up, so you practice catching the pattern instead of the answer. When that same setup shows up on test day, you see it coming.",
  },
  {
    q: "So how is this different from doing more practice questions?",
    a: "Grinding hundreds of new questions mostly drills content you already know. We line up questions that all hide the same trap, one after another, so the pattern clicks. After a short session you start spotting the trap before you even read the choices. That is the part most prep skips.",
  },
  {
    q: "How is this different from an app that just gives you the answer?",
    a: "Some apps just hand you the answer. That feels productive, but on test day it is just you and four choices. We do the opposite: we show you why the tempting wrong answers are built to look right, so you learn to catch them yourself. And every question is independently checked before it ships, so you are never practicing against a wrong answer key.",
  },
  {
    landing: true,
    q: "How do I know the questions and answer keys are actually correct?",
    a: `Every question is solved from scratch and independently checked before it ships, so the right answer is right and the wrong ones are real traps. We also re-checked all ${CORPUS_TOTAL_LABEL} questions in one pass and fixed every error we found. If you ever spot one that looks off, flag it in a tap and a person reviews it. The full process is written up on our methodology page.`,
  },
  {
    landing: true,
    q: "I've run out of official practice tests. Can this help?",
    a: "Yes, that is one of the most common reasons people show up here. Once you have been through the official Bluebook tests, you need more that behave like the real exam, including the harder second module that catches people off guard. We have 50+ full-length practice tests across the digital SAT and the ACT, scored the way the real exams are (the SAT on the College Board's published conversion, the ACT on a 1 to 36 scale).",
  },
  {
    q: "Do you have full-length ACT practice tests?",
    a: "Yes. There are full-length ACT practice tests with authentic English, Math, Reading, and Science sections, scored 1 to 36 with a composite, plus shorter single-section tests when you only want to drill one area. Like the SAT tests, they are built to match the current exam, not leaked from it.",
  },
  {
    q: "Can I practice the ACT Science section?",
    a: "Yes. ACT Science is one of the trickiest sections because it tests how you read graphs, tables, and experiments under time, not memorized facts. We have a full ACT Science question bank plus Science sections inside the full-length ACT tests, so you can practice the reasoning that section actually rewards.",
  },
  {
    landing: true,
    q: "Should I take the SAT or the ACT?",
    a: "Colleges accept both equally, so take the one you score better on. The digital SAT is shorter and adaptive, with a built-in calculator for all of Math. The ACT is faster per question and includes a Science section. The quickest way to decide is to try a short drill of each here and see which one feels more natural, then focus your practice there.",
  },
  {
    q: "Why do I keep getting them wrong even when I know the material?",
    a: "That usually means the questions are doing their job. The wrong answers are designed to look right, and most prep never shows you why you reached for them. Every drill ends by showing you the exact reason each tempting answer is wrong, so the same trap stops working on you.",
  },
  {
    q: "Why sign in with Google?",
    a: "We use Google to confirm who you are and save your progress across devices. We request your email and basic profile information only (name, picture). We do not access your Gmail, Drive, Classroom, or any other Google service.",
  },
  {
    q: "What data do you collect?",
    a: "Your name, email, and Google profile picture, plus the questions you've answered, how long you took, and which ones you got right or wrong. That data only powers your dashboard and stays inside our database. We do not sell it.",
  },
  {
    landing: true,
    q: "Is it free?",
    a: "Yes, fully free to use. No paywall, no credit card, and no ads. If we ever add paid features, we'll tell you in advance and won't take away anything you already have.",
  },
  {
    q: "Will it work on my phone?",
    a: "Yes. Open the site in Chrome or Safari, then add it to your home screen and it runs like an app. No app store, no install nag.",
  },
  {
    q: "How long does a session take?",
    a: "About five minutes for a module drill. Some module drills are shorter. The full practice test is about two hours and you can pause between sections.",
  },
  {
    landing: true,
    q: "Where does the practice-test score come from?",
    a: "Your SAT sections are converted with the College Board's published raw-to-scaled tables, and ACT sections use the published 1 to 36 scale. The conversion is official, the questions are ours, so read the number as a solid estimate rather than an official score. Give it about a 60-point band on the SAT, since real scores move between forms and test days.",
  },
  {
    q: "Can I delete my account?",
    a: "Yes. Email support@breakthetest.app from the address you used to sign up. We will delete your account and all attempt data within seven days. You can also read the full process on the Support page.",
  },
  {
    q: "Are you affiliated with the College Board?",
    a: "No. SAT, AP, PSAT, and Bluebook are registered trademarks of the College Board. They are not affiliated with, and do not endorse, Break the Test. Same for ACT and Common App.",
  },
];
